package com.appsyde.contract.recommended

/**
 * Poids P0/P1/P2 pour les clés (Spec §5). Seul P0 pilote l'action recommandée.
 * Partagé front/back pour le même calcul.
 */
object CognitiveWeight {

    /** §5.2: signal livraison pour missing_item (delivery_address/recipient → P1). */
    fun hasDeliverySignal(state: RecommendedActionState): Boolean {
        if (state.detected["delivery_proof"] == "provided") return true
        if ("delivery_proof" in state.toConfirm) return true
        if (state.intent?.trim()?.lowercase() == "order_tracking") return true
        return false
    }

    /** §5.3 + §5.4: weight(intent, key, state) → P0 | P1 | P2. */
    fun weight(intent: String?, key: String, state: RecommendedActionState): Weight {
        val k = key.trim().lowercase()
        val i = intent?.trim()?.lowercase() ?: return Weight.P2

        return when (i) {
            "damaged_product" -> when (k) {
                "order_number" -> Weight.P0
                "photo_proof", "refund_preference" -> Weight.P0
                "product_name", "product_sku", "account_id" -> Weight.P1
                else -> if (k.startsWith("delivery_") || k.startsWith("customer_")) Weight.P2 else Weight.P1
            }
            "wrong_item" -> when (k) {
                "order_number", "product_sku", "photo_proof", "refund_preference" -> Weight.P0
                "product_name", "account_id" -> Weight.P1
                else -> if (k.startsWith("delivery_") || k.startsWith("customer_")) Weight.P2 else Weight.P1
            }
            "missing_item" -> when (k) {
                "order_number", "missing_product_name", "refund_preference" -> Weight.P0
                "tracking_number", "product_sku", "delivery_proof" -> Weight.P1
                "delivery_address", "delivery_recipient" -> if (hasDeliverySignal(state)) Weight.P1 else Weight.P2
                else -> Weight.P2
            }
            "order_tracking" -> when (k) {
                "order_number", "tracking_number" -> Weight.P0
                "delivery_proof" -> Weight.P1
                "delivery_address", "delivery_recipient" -> Weight.P2
                else -> Weight.P2
            }
            "refund_request" -> when (k) {
                "order_number", "refund_preference" -> Weight.P0
                "amount", "date_reference", "account_id" -> Weight.P1
                else -> if (k == "photo_proof" || k.startsWith("delivery_")) Weight.P2 else Weight.P1
            }
            "cancellation" -> when (k) {
                "order_number" -> Weight.P0
                "date_reference", "amount", "account_id" -> Weight.P1
                else -> Weight.P2
            }
            "billing_question" -> when (k) {
                "invoice_number", "order_number" -> Weight.P0
                "amount", "date_reference", "account_id" -> Weight.P1
                else -> Weight.P2
            }
            "password_reset" -> when (k) {
                "login_identifier" -> Weight.P0
                "recent_security_events" -> Weight.P1
                else -> Weight.P2
            }
            "bug_report" -> when (k) {
                "account_id" -> Weight.P0
                "date_reference" -> Weight.P1
                else -> Weight.P2
            }
            "product_question", "general_question" -> when (k) {
                "product_name", "product_sku" -> Weight.P1
                else -> Weight.P2
            }
            else -> Weight.P2
        }
    }
}
